import type { IResource, ISchedule, IUserFull } from "@gettactic/api";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { formatWalkUpOccupancy } from "@gettactic/helpers/src/schedules/formatWalkUpOccupancy";
import { toZonedTime } from "date-fns-tz";
import {
  assigneeCount,
  formatReleasedUntil,
  hasResourceAssignment
} from "./resourceStatus";

/**
 * The single status line shown on the minimal map hover preview. Either a small
 * colored badge (a state word) or the person currently occupying the resource.
 */
export type HoverStatusTone = "green" | "blue" | "amber" | "neutral";
export type HoverBadgeIcon = "dot" | "half" | "shield" | "ban" | "user";

/**
 * A quieter second line under a badge, kept short because the card is
 * narrow: "Normally dedicated to" followed by the owner's avatar.
 */
export interface HoverStatusDetail {
  text: string;
  user?: Partial<IUserFull>;
}

export type HoverStatus =
  | {
      kind: "badge";
      tone: HoverStatusTone;
      icon: HoverBadgeIcon;
      label: string;
      /** Render the label in strong dark text (used for a room's meeting name). */
      strong?: boolean;
      detail?: HoverStatusDetail;
    }
  | { kind: "person"; user: Partial<IUserFull> }
  /** Restricted to several people/teams the viewer isn't one of. */
  | { kind: "assignees"; count: number };

/** Fields the hover status derivation depends on, plus the resolved schedules. */
type HoverStatusResource = Pick<
  IResource,
  | "type"
  | "is_open"
  | "is_available"
  | "is_approvable"
  | "is_dedicated"
  | "is_walkup"
  | "available_slots"
  | "reserved_slots"
  | "visible_assignment"
  | "user_visible_assignments"
  | "team_visible_assignments"
> &
  Partial<Pick<IResource, "dedicated_exception">> & { schedules?: ISchedule[] };

interface HoverStatusOptions {
  now: Date;
  /** True when org privacy hides occupant/owner identity for this resource. */
  hideIdentity: boolean;
  /**
   * True when an admin switched the resource off (see isResourceOutOfService).
   * `is_available` alone can't tell that apart from "not bookable by me".
   */
  isOutOfService: boolean;
}

export type HoverBadge = Extract<HoverStatus, { kind: "badge" }>;

const badge = (
  tone: HoverStatusTone,
  icon: HoverBadgeIcon,
  label: string,
  strong = false
): HoverBadge => ({ kind: "badge", tone, icon, label, strong });

/**
 * Parse a schedule timestamp the same way the rest of the app does: the
 * backend's `start`/`end` carry a trailing `Z` but actually represent
 * wall-clock time, not a real UTC instant — `dateStringToLocalTime` strips
 * it so the string parses as browser-local (see its many other call sites,
 * e.g. TimelineEventBlock, DashboardEventDetails, ScheduleInfoCard). `now`
 * (a real `Date`) is directly comparable once parsed this way.
 */
const parseScheduleTime = (value: string): number =>
  new Date(dateStringToLocalTime(value)).getTime();

const findActiveSchedule = (
  schedules: ISchedule[] | undefined,
  now: number
): ISchedule | undefined =>
  schedules?.find((s) => {
    // An open-ended schedule (no end yet) is inherently ongoing right now —
    // a walk-up check-in in particular is created at the moment of
    // check-in, so there's no legitimate "hasn't started yet" case for one.
    // Check this before the `now < start` guard below (not after), or a
    // stale `now` (this popover's `now` is the map's own queried date/time,
    // not necessarily this instant) can wrongly reject an already-ongoing
    // check-in just because it started after that queried time.
    if (!s.end) return true;
    if (now < parseScheduleTime(s.start)) return false;
    return now <= parseScheduleTime(s.end);
  });

/**
 * Derives the one hover status line.
 *
 * The hover answers "who has this desk right now / can I take it?", so it
 * leads with the active reservation and otherwise falls through to the
 * desk's availability state.
 *
 * Restricted-access assignees (`visible_assignment`,
 * `user_visible_assignments`, `team_visible_assignments`) are deliberately
 * never *named* here: they say who is *allowed* to book the desk, not who
 * booked it, so leading with them made a desk read as taken by someone with
 * no reservation. They only surface as a count, and only when they are the
 * reason the viewer can't book the desk. The click popover owns the list
 * (see ResourceAssignments).
 */
export const getResourceHoverStatus = (
  resource: HoverStatusResource,
  { now, hideIdentity, isOutOfService }: HoverStatusOptions
): HoverStatus => {
  const isRoom = resource.type === "meeting_room";

  // 1. Occupied right now → lead with who's in it (desk) or the meeting (room).
  // A walk-up is occupied ONLY via a still-open (no end) check-in — its
  // schedule records with both a start and end are just closed past
  // check-ins, never "reservations" that could be "in progress" the way a
  // real advance-booking window can, so `now`/window comparison must not
  // apply to them at all (matches hasActiveResourceSchedule, the click
  // path's exact logic, so the two can never disagree).
  const active = resource.is_walkup
    ? resource.schedules?.find((s) => !s.end)
    : findActiveSchedule(resource.schedules, now.getTime());
  if (active) {
    if (isRoom) {
      return badge("green", "dot", active.title || "In use", true);
    }
    const occupant = active.organizer ?? active.user ?? active.slots?.[0]?.user;
    // A walk-up check-in with no real person attached is stamped with a
    // "walkup" placeholder identity — show that it's occupied, never a
    // fabricated person.
    if (occupant?.roles?.includes("walkup")) {
      return badge("neutral", "dot", "Occupied");
    }
    if (hideIdentity || !occupant) {
      // Walk-up occupancy without a known end is a check-in, not a
      // booking — "Reserved" would be misleading since nobody reserved it
      // in advance, so lead with when it started instead. Once it has a
      // real end (e.g. checked out, or the external system set one), it
      // reads the same as any other booking.
      return badge(
        "neutral",
        "dot",
        resource.is_walkup && !active.end
          ? formatWalkUpOccupancy(
              new Date(dateStringToLocalTime(active.start)),
              // `active.start` is read as raw office wall-clock digits (no
              // real conversion) — "now" must be expressed the same way,
              // or the "today" comparison would use the viewer's own
              // timezone instead of the office's.
              toZonedTime(now, active.time_zone)
            )
          : "Reserved"
      );
    }
    return { kind: "person", user: occupant };
  }

  // 2. Dedicated desk, free right now (case 1 already named the occupant
  // when there is one) — say what it is instead of the generic
  // "Unavailable" it would otherwise fall into. One released for the day
  // while its owner is away is bookable: say until when, and that it is
  // not a hot desk the rest of the time.
  if (resource.is_dedicated) {
    if (resource.dedicated_exception) {
      // Being released does not make the desk bookable on its own, so the
      // states that say it cannot be taken still come first — the same
      // order getResourceStatus (the click path) uses, so the hover and the
      // card can never disagree on a booked or out-of-service released desk.
      if (!resource.is_available) {
        return badge("neutral", "ban", "Unavailable");
      }
      if (resource.available_slots <= 0) {
        return badge("neutral", "dot", "Reserved");
      }
      // On a released day the visible assignments name the release's
      // grantees, never the owner, so the owner is only the one the release
      // itself names.
      const owner = hideIdentity
        ? undefined
        : resource.dedicated_exception.dedicated_user;
      return {
        ...badge(
          "green",
          "dot",
          `Available until ${formatReleasedUntil(resource.dedicated_exception.ends_on)}`
        ),
        detail: owner
          ? { text: "Normally dedicated to", user: owner }
          : { text: "Normally a dedicated desk" }
      };
    }
    return badge("neutral", "dot", "Dedicated");
  }

  // 2b. Walk-up resource, free right now — never approval-gated or
  // partially-available in the advance-booking sense, so it skips straight
  // to its own badge instead of falling through 3-6. is_available/
  // available_slots reflect the whole queried window (or are simply
  // unreliable for this resource type) and must NOT factor in at all — a
  // walk-up only ever matters right now, and the active-schedule check
  // above (section 1) is the only real occupancy signal. Matches
  // getResourceStatus (the click path).
  if (resource.is_walkup) {
    return badge("blue", "dot", "Walk-up");
  }

  // 3-6. Generic bookable states, in the same precedence getResourceStatus
  // (the click path) uses — "can I take it?" before "what kind of desk is
  // it?" — so the hover and the card can never disagree.
  if (!resource.is_available) {
    // Not bookable by the viewer, but not switched off either: on a desk
    // restricted to other people say so, instead of the same "Unavailable" a
    // desk toggled off gets — the two looked identical on hover (sc-8877).
    if (!isOutOfService && hasResourceAssignment(resource)) {
      const count = assigneeCount(resource) || 1;
      if (count > 1) {
        return { kind: "assignees", count };
      }
      return badge("neutral", "user", "Assigned desk");
    }
    return badge("neutral", "ban", "Unavailable");
  }
  // No capacity left for the queried window, even though the desk is still in
  // service. Section 1 only names an occupant whose booking covers `now` (the
  // map's queried date/time), so a desk booked for a *later* slot that day
  // reaches here fully booked: without this it fell through to "Available to
  // Reserve" while the card read RESERVED and the map spot wasn't reservable.
  if (resource.available_slots <= 0) {
    return badge("neutral", "dot", "Reserved");
  }
  if (resource.is_approvable) {
    return badge("amber", "shield", "Approval required");
  }
  if (resource.reserved_slots > 0 && resource.available_slots > 0) {
    return badge("blue", "half", "Partially available");
  }
  return badge("green", "dot", "Available to Reserve");
};
